/**
 * intent-recognition — 意图识别 + 工具调用模块
 *
 * 每次问答时调用大模型判断用户意图，
 * 大模型返回结构化 JSON 描述需要调用的工具及参数，
 * 本模块执行工具并返回结果，否则返回 noNeed。
 *
 * 工具实现统一放在 ./tools/ 目录下，每个工具一个文件，
 * 在 toolRegistry 中注册即可自动接入。
 */

import { sendMessageStream, type Live2DMessage } from '@/service/live2d';
import request from '@/utils/request';
import { searchArticles, type ArticleSearchParams } from './tools/searchArticles';

// ==================== 工具注册表 ====================

const toolRegistry = {
  searchArticles,
};

type ToolName = keyof typeof toolRegistry;

// ==================== 返回类型 ====================

export type IntentResult =
  | { type: 'noNeed'; data: null }
  | { type: ToolName; data: string };

// ==================== 工具函数 ====================

/** 读取 SSE 流，累积 content 返回完整文本 */
async function readFullResponse(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  signal?: AbortSignal,
): Promise<string> {
  const decoder = new TextDecoder();
  let buffer = '';
  let fullReply = '';
  while (true) {
    if (signal?.aborted) break;
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || !trimmed.startsWith('data: ')) continue;
      try {
        const chunk = JSON.parse(trimmed.slice(6).trim());
        if (chunk.type === 'content' && chunk.content) fullReply += chunk.content;
      } catch {
        /* skip */
      }
    }
  }
  return fullReply;
}

/** 标签数据类型（/select/tags 返回格式） */
interface TagItem {
  id?: number;
  value: string;
  label: string;
}

/** 通过标签 label 匹配对应的 value，调用 /select/tags 接口 */
async function resolveTagValue(label: string): Promise<string | null> {
  try {
    const res = await request.get('/select/tags');
    const tags: TagItem[] = Array.isArray(res.data) ? res.data : [];
    const lower = label.toLowerCase().trim();
    // 先精确匹配 label，再精确匹配 value，再模糊匹配
    const matched =
      tags.find((t) => t.label.toLowerCase() === lower) ||
      tags.find((t) => t.value.toLowerCase() === lower) ||
      tags.find((t) => t.label.toLowerCase().includes(lower)) ||
      tags.find((t) => t.value.toLowerCase().includes(lower));
    return matched?.value ?? null;
  } catch {
    return null;
  }
}

// ==================== 大模型分类提示词 ====================

const CLASSIFY_PROMPT = `你是一个意图识别助手。你的任务是根据用户输入判断是否需要调用特定工具，并提取相关参数。

## 可用工具
- searchArticles：用户想要查看文章、博客、推荐文章等与博客文章相关的请求

## 参数说明
- isFeatured: string — 请求"推荐"或"精选"文章时设为 "1"
- pageSize: number — 当用户明确指定文章数量时设置（如"推荐5篇文章" → 5）
- tag: string — 当用户提到特定主题/分类（如"React相关文章" → "react"），填入标签的 label 或 value。如果不知道确切的 value，填入用户说的主题词即可

## 输出格式
只输出 JSON，不要包含任何其他内容。
如果需要调用工具：{"type":"searchArticles","params":{...}}
如果不需要调用任何工具：{"type":"noNeed"}

## 示例
用户：有什么推荐的文章吗
输出：{"type":"searchArticles","params":{"isFeatured":"1"}}

用户：你好呀
输出：{"type":"noNeed"}

用户：最近有什么好看的博客
输出：{"type":"searchArticles","params":{"isFeatured":"1"}}

用户：今天过得怎么样
输出：{"type":"noNeed"}

用户：推荐几篇博客看看
输出：{"type":"searchArticles","params":{"pageSize":3}}

用户：推荐5篇文章
输出：{"type":"searchArticles","params":{"pageSize":5}}

用户：推荐7篇React相关的文章
输出：{"type":"searchArticles","params":{"pageSize":7,"tag":"react"}}

用户：有没有JavaScript的文章
输出：{"type":"searchArticles","params":{"tag":"JavaScript"}}

用户：推荐看看
输出：{"type":"searchArticles","params":{"pageSize":3}}`;

// ==================== 主入口 ====================

/**
 * 检测用户意图并执行对应工具
 * @param input 用户输入内容
 * @returns IntentResult
 *   - { type: 'noNeed', data: null } 不需要调用工具
 *   - { type: 'searchArticles', data: '...' } 工具执行结果
 */
export async function detectAndExecute(input: string): Promise<IntentResult> {
  const messages: Live2DMessage[] = [
    { role: 'system', content: CLASSIFY_PROMPT },
    { role: 'user', content: input },
  ];

  try {
    const controller = new AbortController();
    const response = await sendMessageStream(messages, controller.signal);

    if (!response.ok || !response.body) {
      return { type: 'noNeed', data: null };
    }

    const raw = (await readFullResponse(response.body.getReader(), controller.signal)).trim();

    // 解析 JSON 结果
    const parsed = JSON.parse(raw);
    if (parsed.type === 'searchArticles') {
      const params: ArticleSearchParams = parsed.params || {};

      // 如果 params 中有 tag 字段（可能是 label 或 value），调用 tags 接口解析
      if (params.tag) {
        const resolvedValue = await resolveTagValue(params.tag);
        if (resolvedValue) {
          params.tag = resolvedValue;
        }
      }

      const data = await toolRegistry.searchArticles(params);
      return { type: 'searchArticles', data };
    }
  } catch {
    // LLM 调用失败或解析失败，降级为无需工具
  }

  return { type: 'noNeed', data: null };
}
